import {Component, inject, Input, OnDestroy} from '@angular/core';
import {MatCardModule} from "@angular/material/card";
import {AgGridAngular} from "ag-grid-angular";
import {ColDef, FirstDataRenderedEvent, GridSizeChangedEvent} from "ag-grid-community";
import {IGamePlayerInputData} from "../../../interfaces/player/IGamePlayerInputData";
import {PlayersService} from "../../../services/players.service";
import {UIStateEnum} from "../../../enum/UIStateEnum";
import {IPlayerPaymentMethodQueryResult} from "../../../interfaces/player/IPlayerPaymentMethodQueryResult";
import {ErrorHandlingService} from "../../../services/error-handling.service";
import {PermissionsService} from "../../../services/permissions.service";
import {PermissionTypes} from "../../../enum/PermissionTypes";
import {IDuplicateCardFingerprintResult} from "../../../interfaces/player/IDuplicateCardFingerprintResult";
import {CommonModule} from "@angular/common";
import {RouterModule} from "@angular/router";
import {catchError, filter, of, Subscription, switchMap, tap} from "rxjs";

@Component({
  selector: 'app-manage-player-payment-methods',
  standalone: true,
  imports: [
    MatCardModule,
    AgGridAngular,
    CommonModule,
    RouterModule
  ],
  templateUrl: './manage-player-payment-methods.component.html',
  styleUrl: './manage-player-payment-methods.component.css'
})
export class ManagePlayerPaymentMethodsComponent implements OnDestroy {
  private playerService: PlayersService = inject(PlayersService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private permissionsService: PermissionsService = inject(PermissionsService);

  public uiState = UIStateEnum.ShowData;
  public uiStateForTemplate = UIStateEnum;
  public paymentMethods: IPlayerPaymentMethodQueryResult[] = [];
  public duplicateCardPlayers: IDuplicateCardFingerprintResult[] = [];
  public canViewDuplicateFingerprints = false;
  public duplicateFingerprintError = false;

  public paymentMethodsTableColDefs: ColDef[] = [
    {
      field: 'CardLastFourDigits',
      headerName: 'Last 4 Digits',
      flex: 1
    },
    {
      field: 'CardExpirationMonth',
      headerName: 'Exp Month',
      flex: 1
    },
    {
      field: 'CardExpirationYear',
      headerName: 'Exp Year',
      flex: 1
    },
    {
      field: 'CardFingerprint',
      headerName: 'Card Fingerprint',
      flex: 2
    }
  ];

  private currentGameId: string = '';
  private fetchSubscription: Subscription | null = null;

  @Input()
  public set gamePlayerData(gamePlayerDataP: IGamePlayerInputData) {
    if (gamePlayerDataP.playerId && gamePlayerDataP.game) {
      this.currentGameId = gamePlayerDataP.game.Id;
      this.canViewDuplicateFingerprints = this.permissionsService.userHasCrossGamePermission(PermissionTypes.ViewDuplicateCardFingerprints);
      this.fetchPaymentMethodsAndDuplicates(gamePlayerDataP.playerId, gamePlayerDataP.game.Id);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  private unsubscribe(): void {
    if (this.fetchSubscription) {
      this.fetchSubscription.unsubscribe();
      this.fetchSubscription = null;
    }
  }

  private fetchPaymentMethodsAndDuplicates(playerIdP: string, gameIdP: string): void {
    this.unsubscribe();
    this.uiState = UIStateEnum.ShowLoading;
    this.paymentMethods = [];
    this.duplicateCardPlayers = [];
    this.duplicateFingerprintError = false;

    this.fetchSubscription = this.playerService.getPlayerPaymentMethods(playerIdP, gameIdP).pipe(
      tap(paymentMethods => {
        this.paymentMethods = paymentMethods;
        this.uiState = UIStateEnum.ShowData;
      }),
      filter(() => this.canViewDuplicateFingerprints),
      switchMap(paymentMethods => {
        const fingerprints = paymentMethods
          .map(pm => pm.CardFingerprint)
          .filter(fp => fp && fp.trim() !== '');

        if (fingerprints.length === 0) {
          return of([]);
        }

        return this.playerService.getOtherPlayersWithSameCardFingerprints(playerIdP, fingerprints).pipe(
          catchError(err => {
            console.error('Failed to check for duplicate card fingerprints', err);
            this.duplicateFingerprintError = true;
            return of([]);
          })
        );
      })
    ).subscribe({
      next: (duplicatePlayers) => {
        this.duplicateCardPlayers = duplicatePlayers;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  getPlayerManageUrl(playerIdP: string): string {
    return `/${this.currentGameId}/manage-player/${playerIdP}`;
  }

  gridSizeChanged(params: GridSizeChangedEvent): void {
    params.api.sizeColumnsToFit();
  }

  onFirstDataRendered(params: FirstDataRenderedEvent): void {
    params.api.sizeColumnsToFit();
  }
}
